import { useState } from 'react';

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { CATEGORY_CONFIG } from '@/utils/category-utils';
import type { TemplateTask } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';

const MONTH_NAMES = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'Select category...' },
  ...Object.entries(CATEGORY_CONFIG).map(([value, cfg]) => ({
    value,
    label: cfg.displayName,
  })),
];

const TIMING_OPTIONS = [
  { value: '', label: 'Select timing...' },
  { value: TimingAnchor.OpenerRelative, label: 'Opener-relative' },
  { value: TimingAnchor.CalendarDate, label: 'Calendar date' },
  { value: TimingAnchor.FrostDate, label: 'Frost date' },
  { value: TimingAnchor.RutRelative, label: 'Rut-relative' },
];

interface TemplateTaskFormErrors {
  title?: string;
  category?: string;
  timingAnchor?: string;
  calendarMonth?: string;
}

interface TemplateTaskFormProps {
  task?: TemplateTask;
  sortOrder: number;
  availableDependencies: TemplateTask[];
  onSave: (task: TemplateTask) => void;
  onCancel: () => void;
}

export function TemplateTaskForm({
  task,
  sortOrder,
  availableDependencies,
  onSave,
  onCancel,
}: TemplateTaskFormProps) {
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [category, setCategory] = useState<TaskCategory | ''>(task?.category ?? '');
  const [timingAnchor, setTimingAnchor] = useState<TimingAnchor | ''>(task?.timingAnchor ?? '');
  const [offsetDays, setOffsetDays] = useState(String(task?.offsetDays ?? '0'));
  const [calendarMonth, setCalendarMonth] = useState(
    task?.calendarMonth !== undefined ? String(task.calendarMonth) : '',
  );
  const [calendarDayStart, setCalendarDayStart] = useState(
    task?.calendarDayRangeStart !== undefined ? String(task.calendarDayRangeStart) : '',
  );
  const [calendarDayEnd, setCalendarDayEnd] = useState(
    task?.calendarDayRangeEnd !== undefined ? String(task.calendarDayRangeEnd) : '',
  );
  const [zoneAdjustable, setZoneAdjustable] = useState(task?.zoneAdjustable ?? false);
  const [propertyTied, setPropertyTied] = useState(task?.propertyTied ?? false);
  const [dependsOn, setDependsOn] = useState<string[]>(task?.dependsOn ?? []);
  const [predefinedActions, setPredefinedActions] = useState(
    task?.predefinedActions.join(', ') ?? '',
  );
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [errors, setErrors] = useState<TemplateTaskFormErrors>({});

  function toggleDependency(depId: string) {
    setDependsOn((prev) =>
      prev.includes(depId) ? prev.filter((d) => d !== depId) : [...prev, depId],
    );
  }

  function validate(): boolean {
    const next: TemplateTaskFormErrors = {};
    if (!title.trim()) next.title = 'Title is required';
    if (!category) next.category = 'Category is required';
    if (!timingAnchor) next.timingAnchor = 'Timing is required';
    if (timingAnchor === TimingAnchor.CalendarDate) {
      const month = parseInt(calendarMonth, 10);
      if (!calendarMonth || isNaN(month) || month < 1 || month > 12) {
        next.calendarMonth = 'Valid month (1-12) is required for calendar date';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSave() {
    if (!validate()) return;

    const newTask: TemplateTask = {
      id: task?.id ?? crypto.randomUUID(),
      templateId: task?.templateId ?? '',
      title: title.trim(),
      description,
      // CAST: validate() ensures category is a valid TaskCategory before reaching here
      category: category as TaskCategory,
      // CAST: validate() ensures timingAnchor is a valid TimingAnchor before reaching here
      timingAnchor: timingAnchor as TimingAnchor,
      offsetDays:
        timingAnchor === TimingAnchor.OpenerRelative
          ? parseInt(offsetDays, 10) || 0
          : 0,
      calendarMonth:
        timingAnchor === TimingAnchor.CalendarDate
          ? parseInt(calendarMonth, 10) || undefined
          : undefined,
      calendarDayRangeStart:
        timingAnchor === TimingAnchor.CalendarDate
          ? parseInt(calendarDayStart, 10) || undefined
          : undefined,
      calendarDayRangeEnd:
        timingAnchor === TimingAnchor.CalendarDate
          ? parseInt(calendarDayEnd, 10) || undefined
          : undefined,
      zoneAdjustable,
      propertyTied,
      dependsOn,
      predefinedActions: predefinedActions
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      sortOrder,
      notes,
    };

    onSave(newTask);
  }

  return (
    <div
      data-testid="task-form"
      className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-4"
    >
      <Input
        label="Title"
        id="task-title"
        value={title}
        onChange={setTitle}
        placeholder="e.g. Hang ladder stands"
        required
        error={errors.title}
      />

      <Textarea
        label="Description"
        id="task-description"
        value={description}
        onChange={setDescription}
        placeholder="Optional task description"
        rows={2}
      />

      <Select
        label="Category"
        id="task-category"
        value={category}
        onChange={(v) => setCategory(v as TaskCategory | '')}
        options={CATEGORY_OPTIONS}
        required
        error={errors.category}
      />

      <Select
        label="Timing"
        id="task-timing"
        value={timingAnchor}
        onChange={(v) => setTimingAnchor(v as TimingAnchor | '')}
        options={TIMING_OPTIONS}
        required
        error={errors.timingAnchor}
      />

      {timingAnchor === TimingAnchor.OpenerRelative && (
        <Input
          label="Offset Days (negative = before opener)"
          id="task-offset-days"
          type="number"
          value={offsetDays}
          onChange={setOffsetDays}
          placeholder="-45"
        />
      )}

      {timingAnchor === TimingAnchor.CalendarDate && (
        <div className="flex flex-col gap-3">
          <Select
            label="Month"
            id="task-calendar-month"
            value={calendarMonth}
            onChange={setCalendarMonth}
            options={[{ value: '', label: 'Select month...' }, ...MONTH_NAMES]}
            required
            error={errors.calendarMonth}
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Day Start"
                id="task-day-start"
                type="number"
                value={calendarDayStart}
                onChange={setCalendarDayStart}
                placeholder="1"
              />
            </div>
            <div className="flex-1">
              <Input
                label="Day End"
                id="task-day-end"
                type="number"
                value={calendarDayEnd}
                onChange={setCalendarDayEnd}
                placeholder="15"
              />
            </div>
          </div>
        </div>
      )}

      {/* Toggles */}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={zoneAdjustable}
            onChange={(e) => setZoneAdjustable(e.target.checked)}
            className="rounded"
          />
          <span className="text-body-sm text-[var(--color-text-primary)]">Zone adjustable</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={propertyTied}
            onChange={(e) => setPropertyTied(e.target.checked)}
            className="rounded"
          />
          <span className="text-body-sm text-[var(--color-text-primary)]">Property-tied</span>
        </label>
      </div>

      {/* Dependencies */}
      {availableDependencies.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="field-label">
            Depends on
          </span>
          <div className="flex flex-col gap-1.5">
            {availableDependencies.map((dep) => (
              <label key={dep.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dependsOn.includes(dep.id)}
                  onChange={() => toggleDependency(dep.id)}
                  className="rounded"
                />
                <span className="text-body-sm text-[var(--color-text-primary)]">{dep.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <Input
        label="Predefined Actions"
        id="task-actions"
        value={predefinedActions}
        onChange={setPredefinedActions}
        placeholder="hang, inspect, trim_lanes (comma-separated)"
      />

      <Textarea
        label="Notes"
        id="task-notes"
        value={notes}
        onChange={setNotes}
        placeholder="Optional notes"
        rows={2}
      />

      {/* Form actions */}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Task
        </Button>
      </div>
    </div>
  );
}
